import {type FC, useRef, useState} from 'react';
import type {PlaylistInfo} from '../types';
import {setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import {SelectButton, TempItem, TempItemLeft, TempItemRight} from '../types/ReplayPlayer.styled.ts';

interface VideoListItemProps {
    video: PlaylistInfo;
    index: number;
    onPlay: (video: PlaylistInfo) => void;
    onDelete: (video: PlaylistInfo) => void;
}

const VideoListItem: FC<VideoListItemProps> = ({video, index, onPlay, onDelete}) => {
    const [focusedButton, setFocusedButton] = useState<'play' | 'delete' | null>(null);
    const itemRef = useRef<HTMLDivElement>(null);

    const {ref: playButtonRef, focused: playButtonFocused} = useFocusable({
        focusKey: `video-play-${index}`,
        onEnterPress: () => onPlay(video),
        onFocus: () => {
            setFocusedButton('play');
            itemRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        },
        onBlur: () => setFocusedButton(null),
        onArrowPress: (direction) => {
            if (direction === 'left') {
                setFocus('player-control-skip-forward');
                return false;
            }
            return true;
        },
    });

    const {ref: deleteButtonRef, focused: deleteButtonFocused} = useFocusable({
        focusKey: `video-delete-${index}`,
        onEnterPress: () => onDelete(video),
        onFocus: () => {
            setFocusedButton('delete');
            itemRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        },
        onBlur: () => setFocusedButton(null),
    });

    const isItemFocused = focusedButton !== null;

    return (
        <TempItem ref={itemRef} $focused={isItemFocused}>
            <TempItemLeft>
                <img src={video.snippet.thumbnails.high?.url} alt="no img" />
                {video.snippet.title ?? '제목 없음'}
            </TempItemLeft>
            <TempItemRight>
                <SelectButton ref={playButtonRef} $focused={playButtonFocused} onClick={() => onPlay(video)}>
                    ▶ 재생
                </SelectButton>
                <SelectButton
                    ref={deleteButtonRef}
                    $focused={deleteButtonFocused}
                    $isDelete
                    onClick={() => onDelete(video)}
                >
                    🗑 삭제
                </SelectButton>
            </TempItemRight>
        </TempItem>
    );
};

export default VideoListItem;
